/**
 * assets.js — Central asset registry for Noble Fruits
 *
 * Defines all textures, spine animations, bitmap fonts, and effects.
 * Assets are conditionally included based on FEATURES_CONFIG flags.
 *
 * Exports:
 *   ASSETS       — Master object consumed by assetsSlice for loading
 *   ASSET_NAME   — Flat { name → url } map for direct cache lookups
 *   getUITexture  — Spritesheet-aware UI texture retrieval
 *   getUITextureAsDataURL — Extract spritesheet sub-texture as base64 (for HTML/CSS)
 */

import * as PIXI from "@pixi/assets";
import { symbolData, COIN_SYMBOL_IDS } from "./SymbolData.js";
import { getAssetsPath, getBasePath } from "./utils/pathConfig";
import { FEATURES_CONFIG } from "./featuresConfig";
import { Settings } from "./settings";

// ---------------------------------------------------------------------------
// Path constants
// ---------------------------------------------------------------------------
const BASE_PATH = getAssetsPath();
const SPRITESHEETS_PATH = `${getBasePath()}/assets/spritesheets`;
const UI_THEME = Settings.APP.UI_THEME;

// ---------------------------------------------------------------------------
// 1. UI SPRITESHEET
// ---------------------------------------------------------------------------
const UI_ASSETS = [
  // Old spritesheet — only needed by primary theme (secondary fully migrated to ui_spritesheet_new)
  ...(UI_THEME === "primary"
    ? [{
        name: "ui_spritesheet",
        url: `${SPRITESHEETS_PATH}/user_interface_primary/user_interface_primary.json`,
      }]
    : []),
  {
    name: "ui_spritesheet_new",
    url: `${SPRITESHEETS_PATH}/user_interface/user_interface.json`,
  },
];

// ---------------------------------------------------------------------------
// 2. UI TEXTURE HELPERS
// ---------------------------------------------------------------------------

/**
 * Get a UI texture from the theme spritesheet by frame name.
 * @param {string} name - Frame name (e.g., "icon_home", "popup_button")
 * @returns {PIXI.Texture|null}
 */
export const getUITexture = (name) => {
  // Try the current theme spritesheet first
  const key = "ui_spritesheet";
  if (PIXI.Assets.cache.has(key)) {
    const sheet = PIXI.Assets.get(key);
    if (sheet?.textures) {
      const tex = sheet.textures[name] || sheet.textures[`${name}.png`];
      if (tex) return tex;
    }
  }
  // Fallback to new spritesheet (migration)
  const newKey = "ui_spritesheet_new";
  if (PIXI.Assets.cache.has(newKey)) {
    const sheet = PIXI.Assets.get(newKey);
    if (sheet?.textures) {
      const tex = sheet.textures[name] || sheet.textures[`${name}.png`];
      if (tex) return tex;
    }
  }
  // Spritesheet not loaded yet — try individually loaded asset
  return PIXI.Assets.cache.has(name) ? PIXI.Assets.get(name) : null;
};

/**
 * Get a UI texture from the NEW spritesheet only.
 * @param {string} name - Frame name
 * @returns {PIXI.Texture|null}
 */
export const getNewUITexture = (name) => {
  const key = "ui_spritesheet_new";
  if (PIXI.Assets.cache.has(key)) {
    const sheet = PIXI.Assets.get(key);
    if (sheet?.textures) {
      const tex = sheet.textures[name] || sheet.textures[`${name}.png`];
      if (tex) {
        // Enable mipmaps + anisotropic filtering for UI textures — they are
        // static spritesheet sprites that get downscaled. Safe because these
        // are NOT dynamic canvas textures (PIXI.Text) which cause black flash.
        if (tex.baseTexture && tex.baseTexture.mipmap !== 2) {
          tex.baseTexture.mipmap = 2; // MIPMAP_MODES.ON — works on non-pow2
          tex.baseTexture.anisotropicLevel = 8; // Sharper at non-uniform scales
        }
        return tex;
      }
    }
  }
  return null;
};

/**
 * Extract a PIXI texture's frame to a base64 data URL via an offscreen canvas.
 * Shared by the UI-spritesheet and standalone-image helpers below.
 * @param {PIXI.Texture|null} texture
 * @param {string} [mime="image/png"] - e.g. "image/png" | "image/webp"
 * @returns {string} data URL or empty string
 */
const textureToDataURL = (texture, mime = "image/png") => {
  if (!texture?.baseTexture?.resource?.source) return "";
  const frame = texture.frame;
  const canvas = document.createElement("canvas");
  canvas.width = frame.width;
  canvas.height = frame.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    texture.baseTexture.resource.source,
    frame.x,
    frame.y,
    frame.width,
    frame.height,
    0,
    0,
    frame.width,
    frame.height,
  );
  return canvas.toDataURL(mime);
};

/**
 * Extract a UI spritesheet sub-texture as a base64 data URL.
 * Useful for HTML <img> tags or CSS background-image.
 * @param {string} name - Frame name
 * @returns {string} data URL or empty string
 */
export const getUITextureAsDataURL = (name) =>
  textureToDataURL(getUITexture(name));

/**
 * Same as getUITextureAsDataURL but reads the NEW UI spritesheet
 * (ui_spritesheet_new) — for icons like icon_autoplay_stop used by the
 * secondary-UI buttons, in HTML <img>/CSS contexts.
 * @param {string} name - Frame name
 * @returns {string} data URL or empty string
 */
export const getNewUITextureAsDataURL = (name) =>
  textureToDataURL(getNewUITexture(name));

/**
 * Extract a preloaded standalone game image (non-spritesheet) as a data URL.
 * Used by HTML <img> tags in React overlays (paytable, bonus buy, etc.).
 * Returns empty string if the asset hasn't been loaded yet.
 * @param {string} name - Asset name as registered in GAME_TEXTURES (e.g., "grand_jackpot")
 * @returns {string} data URL or empty string
 */
export const getGameImageSrc = (name) => {
  try {
    if (!PIXI.Assets.cache.has(name)) return "";
    return textureToDataURL(PIXI.Assets.get(name), "image/webp");
  } catch {
    return "";
  }
};


// ---------------------------------------------------------------------------
// 3. GAME TEXTURES — Backgrounds, logos, paylines, feature graphics
// ---------------------------------------------------------------------------
const GAME_TEXTURES = [
  // Logo
  { name: "logo", url: `${BASE_PATH}/images/logo.webp` },

  // Mini paytable overlay — only loaded when feature is enabled
  ...(FEATURES_CONFIG.MINI_PAYTABLE.ENABLED
    ? [{ name: "mini_paytable", url: `${BASE_PATH}/images/mini_paytable.webp` }]
    : []),

  // Divider base overlay (static texture rendered on top of reelset)
  { name: "divider_base", url: `${BASE_PATH}/images/divider_base.webp` },

  // Divider feature overlay (bonus/feature skin variant)
  { name: "divider_feature", url: `${BASE_PATH}/images/divider_feature.webp` },

  // Pot text labels (injected into game_view spine under pot orbs)
  { name: "pot_mystery_txt", url: `${BASE_PATH}/images/pot_mystery_txt.webp` },
  { name: "pot_double_txt",  url: `${BASE_PATH}/images/pot_double_txt.webp` },
  { name: "pot_collect_txt", url: `${BASE_PATH}/images/pot_collect_txt.webp` },

  // Base game background (3840×2580 split into 4 quadrants)
  // Only loaded when QUADRANT_SPRITES is enabled — GameView spine handles the
  // background entirely when GAME_VIEW.ENABLED, making these redundant.
  ...(FEATURES_CONFIG.BACKGROUND.QUADRANT_SPRITES
    ? [
        { name: "base_top_left", url: `${BASE_PATH}/images/base_top_left.webp` },
        { name: "base_top_right", url: `${BASE_PATH}/images/base_top_right.webp` },
        { name: "base_bottom_left", url: `${BASE_PATH}/images/base_bottom_left.webp` },
        { name: "base_bottom_right", url: `${BASE_PATH}/images/base_bottom_right.webp` },
      ]
    : []),

  // Feature game background quadrants (Hold and Win only, quadrant mode only)
  ...(FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && FEATURES_CONFIG.BACKGROUND.QUADRANT_SPRITES
    ? [
        { name: "feature_top_left", url: `${BASE_PATH}/images/feature_top_left.webp` },
        { name: "feature_top_right", url: `${BASE_PATH}/images/feature_top_right.webp` },
        { name: "feature_bottom_left", url: `${BASE_PATH}/images/feature_bottom_left.webp` },
        { name: "feature_bottom_right", url: `${BASE_PATH}/images/feature_bottom_right.webp` },
      ]
    : []),

  // Buy Bonus feature graphics (Bonus Buy only) — secondary theme uses static HTML images
  ...(FEATURES_CONFIG.BONUS_BUY.ENABLED && UI_THEME === "primary"
    ? [
        { name: "free_spin_feature_graphics", url: `${BASE_PATH}/images/free_spin_feature_graphics.webp` },
      ]
    : []),

  // Zeus Respin paytable graphics (always present in build)
  ...(FEATURES_CONFIG.BONUS_BUY.ENABLED
    ? [
        { name: "zeus_respins", url: `${BASE_PATH}/images/zeus_respins.webp` },
        { name: "zeus_respins_features", url: `${BASE_PATH}/images/zeus_respins_features.webp` },
      ]
    : []),

  // Secondary theme paytable images — preloaded during loading screen so the
  // paytable opens instantly with no on-demand fetches.
  ...(UI_THEME === "secondary"
    ? [
        // Feature slides
        { name: "grand_jackpot",         url: `${BASE_PATH}/images/grand_jackpot.webp` },
        // Symbol slide 1 & 2
        { name: "coin_symbol",           url: `${BASE_PATH}/images/coin_symbol.webp` },
        { name: "jackpot_coin_symbols",  url: `${BASE_PATH}/images/jackpot_coin_symbols.webp` },
        { name: "blue_zeus_symbol",      url: `${BASE_PATH}/images/blue_zeus_symbol.webp` },
        { name: "green_zeus_symbol",     url: `${BASE_PATH}/images/green_zeus_symbol.webp` },
        { name: "red_zeus_symbol",       url: `${BASE_PATH}/images/red_zeus_symbol.webp` },
        // Paylines (both orientations — we don't know orientation at load time)
        { name: "paylines_portrait",     url: `${BASE_PATH}/images/paylines_portrait.webp` },
        { name: "paylines_landscape",    url: `${BASE_PATH}/images/paylines_landscape.webp` },
        // Decorative
        { name: "glow",                  url: `${BASE_PATH}/images/glow.webp` },
        // Intro screen — composed layout assets (background + per-feature images)
        // Both orientations preloaded (orientation unknown at load time).
        ...(!FEATURES_CONFIG.INTRO_SCREEN.SPINE_ENABLED ? [
          { name: "intro_background_portrait",  url: `${BASE_PATH}/images/intro_background_portrait.webp` },
          { name: "intro_background_landscape", url: `${BASE_PATH}/images/intro_background_landscape.webp` },
          // Split pot + coins assets (top pot + bottom coins) — used by both the
          // intro slider and the pot-collection "YOU WON" dialog, plus super turbo.
          { name: "intro_feature_mystery_pot",       url: `${BASE_PATH}/images/intro_feature_mystery_pot.webp` },
          { name: "intro_feature_mystery_coins",     url: `${BASE_PATH}/images/intro_feature_mystery_coins.webp` },
          { name: "intro_feature_collect_pot",       url: `${BASE_PATH}/images/intro_feature_collect_pot.webp` },
          { name: "intro_feature_collect_coins",     url: `${BASE_PATH}/images/intro_feature_collect_coins.webp` },
          { name: "intro_feature_double_pot",        url: `${BASE_PATH}/images/intro_feature_double_pot.webp` },
          { name: "intro_feature_double_coins",      url: `${BASE_PATH}/images/intro_feature_double_coins.webp` },
          { name: "intro_feature_superturbo",        url: `${BASE_PATH}/images/intro_feature_superturbo.webp` },
          { name: "zeus_respins_logo",          url: `${BASE_PATH}/images/zeus_respins_logo.webp` },
        ] : []),
      ]
    : []),


];


// ---------------------------------------------------------------------------
// 4. SYMBOL SPINES — Auto-generated from SymbolData
// ---------------------------------------------------------------------------
const symbolSpines = Object.keys(symbolData)
  .filter((key) => {
    const symbol = symbolData[key];
    return symbol?.spine && typeof symbol.spine === "string" && symbol.spine.trim().length > 0;
  })
  .map((key) => {
    const spineName = symbolData[key].spine;
    // Guard against corrupted spine names (e.g., HTML responses cached in dev)
    if (/[<>]|DOCTYPE|html|window|response/.test(spineName)) return null;
    return {
      name: spineName,
      json: `${BASE_PATH}/spines/reel_symbol/${spineName}.json`,
    };
  })
  .filter(Boolean)
  .filter((spine, i, self) => i === self.findIndex((s) => s.name === spine.name));

// ---------------------------------------------------------------------------
// 5. SPINE ANIMATIONS — Conditionally built from feature flags
// ---------------------------------------------------------------------------
const buildSpineAnimations = () => {
  const spines = [];

  // -- Core (always loaded) ------------------------------------------------
  if (FEATURES_CONFIG.GAME_VIEW.ENABLED) {
    // GameView spine replaces background_animation + reel_background.
    // Logo is embedded inside game_view spine — standalone logo spine not needed.
    // intro_screen spine only loaded when INTRO_SCREEN.SPINE_ENABLED — secondary theme uses static fallback.
    spines.push(
      { name: "game_view", json: `${BASE_PATH}/spines/game_view/game_view.json` },
      { name: "win_celebration", json: `${BASE_PATH}/spines/win_celebration/win_celebration.json` },
    );
    if (FEATURES_CONFIG.INTRO_SCREEN.SPINE_ENABLED) {
      spines.push({ name: "intro_screen", json: `${BASE_PATH}/spines/intro_screen/intro_screen.json` });
    }
  } else {
    spines.push(
      { name: "background_animation", json: `${BASE_PATH}/spines/background_animation/background_animation.json` },
      { name: "win_celebration", json: `${BASE_PATH}/spines/win_celebration/win_celebration.json` },
      { name: "reel_background", json: `${BASE_PATH}/spines/reel_background/reel_background.json` },
      { name: "logo", json: `${BASE_PATH}/spines/logo/logo.json` },
    );
    if (FEATURES_CONFIG.INTRO_SCREEN.SPINE_ENABLED) {
      spines.push({ name: "intro_screen", json: `${BASE_PATH}/spines/intro_screen/intro_screen.json` });
    }
  }

  // -- Jackpots ------------------------------------------------------------
  // When GAME_VIEW is enabled, jackpots are rendered inside the game_view spine
  // via bone slots + styled PIXI.Text — the standalone jackpots spine is not used.
  if (FEATURES_CONFIG.JACKPOTS.ENABLED && !FEATURES_CONFIG.GAME_VIEW.ENABLED) {
    spines.push({ name: "jackpots", json: `${BASE_PATH}/spines/jackpots/jackpots.json` });
  }

  // -- Character animation -------------------------------------------------
  if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
    spines.push({ name: "character", json: `${BASE_PATH}/spines/character/character.json` });
  }

  // -- iOS fullscreen overlay ----------------------------------------------
  if (FEATURES_CONFIG.IOS_FULLSCREEN_OVERLAY.ENABLED) {
    spines.push({ name: "ios_scroll", json: `${BASE_PATH}/spines/ios_scroll/ios_scroll.json` });
  }

  // -- Dividers ------------------------------------------------------------
  if (FEATURES_CONFIG.DIVIDERS.ENABLED) {
    spines.push({ name: "divider", json: `${BASE_PATH}/spines/divider/divider.json` });
  }

  // -- Expanding Wilds (full-reel mode only) --------------------------------
  if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel") {
    spines.push({ name: "wild_expand", json: `${BASE_PATH}/spines/wild_expand/wild_expand.json` });
  }

  // -- Tension (anticipation spine replaces old tension) --------------------
  if (FEATURES_CONFIG.TENSION.ENABLED) {
    spines.push({ name: "anticipation", json: `${BASE_PATH}/spines/anticipation/anticipation.json` });
  }

  // -- Free Spins ----------------------------------------------------------
  if (FEATURES_CONFIG.FREE_SPINS.ENABLED) {
    spines.push(
      { name: "free_spin_transition", json: `${BASE_PATH}/spines/free_spin_transition/free_spin_transition.json` },
      { name: "free_spin_counter", json: `${BASE_PATH}/spines/free_spin_counter/free_spin_counter.json` },
      { name: "symbol_flip", json: `${BASE_PATH}/spines/symbol_flip/symbol_flip.json` },
    );
  }

  // -- Win Lines -----------------------------------------------------------
  if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
    spines.push({ name: "win_lines", json: `${BASE_PATH}/spines/win_lines/win_lines.json` });
  }

  // -- Win Box -------------------------------------------------------------
  if (FEATURES_CONFIG.WIN_BOX.ENABLED) {
    spines.push({ name: "win_box", json: `${BASE_PATH}/spines/win_box/win_box.json` });
  }

  // -- Win Line Digits -----------------------------------------------------
  if (FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED) {
    spines.push({ name: "winline_text", json: `${BASE_PATH}/spines/winline_text/winline_text.json` });
  }

  // -- Hold and Win --------------------------------------------------------
  if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
    spines.push(
      { name: "transition_eye", json: `${BASE_PATH}/spines/transition/transition_eye.json` },
      { name: "hns_counter", json: `${BASE_PATH}/spines/hns_counter/hns_counter.json` },
      { name: "hns_multiplier", json: `${BASE_PATH}/spines/hns_multiplier/hns_multiplier.json` },
      { name: "hns_collector", json: `${BASE_PATH}/spines/hns_collector/hns_collector.json` },
      { name: "hns_coin_collect", json: `${BASE_PATH}/spines/hns_coin_collect/hns_coin_collect.json` },
      { name: "hns_end", json: `${BASE_PATH}/spines/hns_end/hns_end.json` },
      { name: "hns_progress", json: `${BASE_PATH}/spines/hns_progress/hns_progress.json` },
      { name: "hns_progress_multiplier", json: `${BASE_PATH}/spines/hns_progress_multiplier/hns_progress_multiplier.json` },
      { name: "hns_progress_tension", json: `${BASE_PATH}/spines/hns_progress_tension/hns_progress_tension.json` },
      { name: "hns_intro", json: `${BASE_PATH}/spines/hns_intro/hns_intro.json` },
      { name: "hns_reload", json: `${BASE_PATH}/spines/hns_reload/hns_reload.json` },
      { name: "total_win", json: `${BASE_PATH}/spines/total_win/total_win.json` },
    );
  }

  // -- Royal Collect -------------------------------------------------------
  if (FEATURES_CONFIG.ROYAL_COLLECT.ENABLED) {
    // Reuses hns_counter spine — only add if H&W didn't already
    if (!FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
      spines.push({ name: "hns_counter", json: `${BASE_PATH}/spines/hns_counter/hns_counter.json` });
    }
  }

  // -- Pot Collection (Zeus Respins) ----------------------------------------
  if (FEATURES_CONFIG.POT_COLLECTION.ENABLED) {
    spines.push(
      { name: "respin_counter", json: `${BASE_PATH}/spines/respin_counter/respin_counter.json` },
      { name: "coin_collector", json: `${BASE_PATH}/spines/coin_collector/coin_collector.json` },
      { name: "symbol_anticipation", json: `${BASE_PATH}/spines/anticipation/symbol_anticipation.json`, atlas: `${BASE_PATH}/spines/anticipation/anticipation.atlas` },
      { name: "respins_left_popup", json: `${BASE_PATH}/spines/respins_left_popup/respins_left_popup.json` },
    );
    // total_win spine NOT needed — this game uses win_celebration for total win display
  }

  // -- Dialogs (shared by Free Spins & Royal Collect) ----------------------
  if (FEATURES_CONFIG.FREE_SPINS.ENABLED || FEATURES_CONFIG.ROYAL_COLLECT.ENABLED) {
    spines.push({ name: "dialogs", json: `${BASE_PATH}/spines/dialogs/dialogs.json` });
  }

  // -- Bonus Buy -----------------------------------------------------------
  // Bonus buy spines — primary theme only.
  // Secondary theme (Zeus) renders the Buy Bonus UI from static assets, not spines.
  if (FEATURES_CONFIG.BONUS_BUY.ENABLED && UI_THEME === "primary") {
    spines.push(
      { name: "bonus_buy", json: `${BASE_PATH}/spines/bonus_buy/bonus_buy.json` },
      { name: "bonus_buy_popup", json: `${BASE_PATH}/spines/bonus_buy_popup/bonus_buy_popup.json` },
    );
  }

  // -- POT Charging (Orb spines) -------------------------------------------
  if (FEATURES_CONFIG.POT_CHARGING.ENABLED) {
    spines.push({ name: "pots", json: `${BASE_PATH}/spines/pots/pots.json` });
  }

  // -- Primary UI spine (loading screen) -----------------------------------
  if (UI_THEME === "primary") {
    spines.push({ name: "loading_screen", json: `${BASE_PATH}/spines/loading_screen/loading_screen.json` });
  }

  // -- Symbol spines (auto-generated from SymbolData) ----------------------
  spines.push(...symbolSpines);

  return spines;
};

// ---------------------------------------------------------------------------
// 6. BITMAP FONTS
// ---------------------------------------------------------------------------
const buildBitmapFonts = () => {
  const fonts = [];

  // Bitmap fonts are only used by the primary UI theme.
  // The secondary theme (Zeus / 3 Orbs) uses styled PIXI.Text exclusively —
  // no BitmapText instances exist in that codebase path.
  if (UI_THEME !== "primary") return fonts;

  // "font" — used by HoldAndWin, FreeSpin counters, and MaxWin/HNS dialogs.
  if (
    FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ||
    FEATURES_CONFIG.FREE_SPINS.ENABLED ||
    FEATURES_CONFIG.ROYAL_COLLECT.ENABLED
  ) {
    fonts.push({ name: "font", json: `${BASE_PATH}/fonts/font/font.fnt` });
  }

  // "font_secondary" — coin value text on reel symbols (primary theme only)
  if (COIN_SYMBOL_IDS.length > 0) {
    fonts.push({ name: "font_secondary", json: `${BASE_PATH}/fonts/font/font_secondary.fnt` });
  }

  // "font_primary" — Jackpots / loading screen (primary theme only)
  if (FEATURES_CONFIG.JACKPOTS.ENABLED) {
    fonts.push({ name: "font_primary", json: `${BASE_PATH}/fonts/font/font_primary.fnt` });
  }

  // "font_blurred" — Hold and Win blurred coin values (primary theme only)
  if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
    fonts.push({ name: "font_blurred", json: `${BASE_PATH}/fonts/font/font_blurred.fnt` });
  }

  return fonts;
};

// ---------------------------------------------------------------------------
// 7. EFFECTS — RevoltFX particle bundles
// ---------------------------------------------------------------------------
const buildEffects = () => {
  const effects = [];

  // Zeus lightning particle effect for POT Charging trails
  if (FEATURES_CONFIG.POT_CHARGING.ENABLED) {
    effects.push({
      name: "zeus_lightning_effect",
      bundle: `${BASE_PATH}/effects/zeus_lightning_particle/zeus_lightning_particle.json`,
      spritesheet: `${BASE_PATH}/effects/zeus_lightning_particle/zeus_lightning_particle_atlas.json`,
    });
  }

  // Rain, trail, and coin shower particle effects for Pot Collection bonus
  if (FEATURES_CONFIG.POT_COLLECTION.ENABLED) {
    effects.push(
      {
        name: "zeus_rain_effect",
        bundle: `${BASE_PATH}/effects/rain/rain.json`,
        spritesheet: `${BASE_PATH}/effects/rain/rain_atlas.json`,
      },
      {
        name: "particle_trail_effect",
        bundle: `${BASE_PATH}/effects/trail/particle_trail.json`,
        spritesheet: `${BASE_PATH}/effects/trail/particle_trail_atlas.json`,
      },
      {
        name: "coin_shower_effect",
        bundle: `${BASE_PATH}/effects/coin_shower/coin_shower.json`,
        spritesheet: `${BASE_PATH}/effects/coin_shower/coin_shower_atlas.json`,
      },
    );
  }

  return effects;
};

// ---------------------------------------------------------------------------
// 8. WEB FONTS — Custom font files for PIXI.Text (loaded via @font-face)
// ---------------------------------------------------------------------------
const buildWebFonts = () => {
  const fonts = [];

  // GrekoNew — main UI font for the secondary theme (Zeus).
  // Also used by primary theme for jackpot value text.
  if (UI_THEME === "secondary") {
    fonts.push({
      name: "GrekoNew",
      url: `${BASE_PATH}/fonts/greko-new/GrekoNew.ttf`,
      format: "truetype",
    });
  }

  // Primary theme web fonts
  if (UI_THEME === "primary" && FEATURES_CONFIG.JACKPOTS.ENABLED) {
    fonts.push({
      name: "GrekoNew",
      url: `${BASE_PATH}/fonts/greko-new/GrekoNew.ttf`,
      format: "truetype",
    });
  }

  return fonts;
};

// ---------------------------------------------------------------------------
// 9. MASTER ASSETS OBJECT — consumed by assetsSlice.js
// ---------------------------------------------------------------------------
export const ASSETS = {
  bitmapFonts: buildBitmapFonts(),
  spineAnimations: buildSpineAnimations(),
  textures: [...UI_ASSETS, ...GAME_TEXTURES],
  effects: buildEffects(),
  webFonts: buildWebFonts(),
};

// ---------------------------------------------------------------------------
// 9. ASSET_NAME MAP — flat { name → url/json } for direct cache lookups
// ---------------------------------------------------------------------------
const generateAssetNames = (assets) => {
  const map = {};
  assets.textures.forEach((a) => { map[a.name] = a.url; });
  assets.spineAnimations.forEach((a) => { map[a.name] = a.json; });
  assets.bitmapFonts.forEach((a) => { map[a.name] = a.json; });
  assets.effects.forEach((a) => { map[a.name] = a.bundle; });
  return map;
};

export const ASSET_NAME = generateAssetNames(ASSETS);
